import { useState } from "react";
import type { ReactNode } from "react";
import { ghostBtnClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { QcSessionSection } from "@/lib/manufacturing/builderAssistantChecklistClient";

function statusFor(section: QcSessionSection | undefined, openFailures: number): string {
  if (!section?.submitted_at) return "Not submitted";
  if (section.result === "pass") return "Passed";
  if (openFailures > 0) return `Submitted · ${openFailures} open ${openFailures === 1 ? "failure" : "failures"}`;
  return "Submitted · failures corrected";
}

export function BuilderAssistantChecklistSection({
  name,
  required,
  included,
  open,
  section,
  openFailures,
  disabled,
  canSubmit,
  onToggleIncluded,
  onToggleOpen,
  onSubmit,
  onCorrect,
  children,
}: {
  name: string;
  required: boolean;
  included: boolean;
  open: boolean;
  section?: QcSessionSection;
  openFailures: number;
  disabled: boolean;
  canSubmit: boolean;
  onToggleIncluded: (selected: boolean) => void;
  onToggleOpen: () => void;
  onSubmit: () => Promise<{ ok: true } | { ok: false; error: string }>;
  onCorrect: () => Promise<{ ok: true } | { ok: false; error: string }>;
  children: ReactNode;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitted = Boolean(section?.submitted_at);
  const status = included ? statusFor(section, openFailures) : "";
  const passed = submitted && section?.result === "pass";
  const failed = submitted && section?.result === "fail" && openFailures > 0;

  async function run(action: () => Promise<{ ok: true } | { ok: false; error: string }>) {
    setBusy(true);
    setError(null);
    const res = await action();
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setConfirming(false);
  }

  return (
    <div className={
      passed
        ? "rounded-lg border border-green-600 dark:border-green-500"
        : failed
          ? "rounded-lg border border-red-500 dark:border-red-700"
          : "rounded-lg border border-gray-200 dark:border-zinc-700"
    }>
      <div className="flex items-center gap-2 px-2 py-2">
        {required ? null : (
          <input
            type="checkbox"
            checked={included}
            disabled={disabled || submitted}
            aria-label={`Include ${name}`}
            onChange={(event) => onToggleIncluded(event.target.checked)}
          />
        )}
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left text-sm font-semibold text-gray-900 dark:text-zinc-100"
          aria-expanded={open}
          onClick={onToggleOpen}
        >
          <span className="min-w-0 truncate">{open ? "▾" : "▸"} {name}{required ? "" : " (optional)"}</span>
          {status ? (
            <span className={
              passed
                ? "shrink-0 text-xs font-medium text-green-700 dark:text-green-300"
                : failed
                  ? "shrink-0 text-xs font-medium text-red-700 dark:text-red-300"
                  : "shrink-0 text-xs font-medium text-gray-500 dark:text-zinc-400"
            }>
              {status}
            </span>
          ) : null}
        </button>
      </div>
      {open && included ? (
        <div className="space-y-2 px-2 pb-2">
          {children}
          {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
          {submitted && openFailures > 0 && !disabled ? (
            <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void run(onCorrect)}>
              Correct failures
            </button>
          ) : null}
          {!submitted && canSubmit && !confirming ? (
            <button type="button" className={mfgPrimaryBtnClass} disabled={disabled || busy} onClick={() => setConfirming(true)}>
              Submit section
            </button>
          ) : null}
          {!submitted && confirming ? (
            <div className="space-y-2 rounded-lg border border-gray-200 p-2 text-sm text-gray-800 dark:border-zinc-700 dark:text-zinc-200">
              <p>Submit {name}? Passed items stay passed. Failures stay open for correction. The rest of this audit stays open.</p>
              <div className="flex gap-2">
                <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={() => void run(onSubmit)}>
                  {busy ? "Submitting…" : "Submit section"}
                </button>
                <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
